import { type ProductizedServiceCardDataWrapper$data } from '#app/__generated__/relay/ProductizedServiceCardDataWrapper.graphql.js';
import { revealLeftToRight } from '#app/components/ContraMagic/animations.js';
import { Link } from '#app/components/Link/index.js';
import { Text } from '#app/components/Primitives/Text.js';
import { type Sortable } from '#app/components/SortableListView/types.js';
import { StyledTagBox } from '#app/components/Tags/Tag.js';
import { TagListMenu } from '#app/components/Tags/TagListMenu/TagListMenu.js';
import { type TagMenuItem } from '#app/components/Tags/TagListMenu/types.js';
import { useAnalytics } from '#app/hooks/useAnalytics.js';
import { useDeleteProductizedService } from '#app/hooks/useDeleteProductizedService.js';
import { useHasUserInterfaceFlags } from '#app/hooks/useHasUserInterfaceFlags.js';
import { useIsMediumScreen } from '#app/hooks/useIsMediumScreen.js';
import { useOnElementViewed } from '#app/hooks/useOnElementViewed.js';
import { MAX_MAIN_LAYOUT_SIZE } from '#app/layouts/MainLayout/MainLayout.js';
import { MaybeDraftAndSuggestedTags } from '../MaybeDraftAndSuggestedTags.js';
import { MaybeEditableCardActionsMenuButton } from '../MaybeEditableCardActionsMenuButton.js';
import { MaybeMobileEditableCardActionsMenu } from '../MaybeMobileEditableCardActionsMenu.js';
import { ProductizedServiceCardDetails } from './ProductizedServiceCardDetails.js';
import { ProductizedServiceCardLinkedProject } from './ProductizedServiceCardLinkedProject.js';
import { ProductizedServiceCardPricing } from './ProductizedServiceCardPricing.js';
import { MoveIcon } from '@contra/icons/icons';
import { getRoute } from '@contra/routes';
import { useCallback, useState } from 'react';
import { css, styled } from 'styled-components';

const Container = styled.div<{
  $visitorCanEdit: boolean;
  isDragging?: boolean;
  isHighlighted?: boolean;
  startRevealProjectAnimation?: boolean;
}>`
  ${({
    $visitorCanEdit,
    isDragging = false,
    startRevealProjectAnimation = false,
    theme,
  }) => css`
    display: grid;
    grid-template-columns: 1fr auto;
    grid-auto-flow: row;

    ${
      $visitorCanEdit
        ? css`
            grid-template-rows: auto auto auto;
            grid-template-areas:
              'cardContent mobileActions'
              'cardTags cardTags'
              'cardActions cardActions';
          `
        : css`
            grid-template-rows: auto auto;
            grid-template-areas:
              'cardContent mobileActions'
              'cardTags cardTags';
          `
    }
   

    border-radius: 10px;

    gap: 16px 0px;
    margin-bottom: 32px;

    background-color: ${theme.colorsV2.white};

    ${theme.mediaQueries.md} {
      gap: 0px 16px;
      margin-bottom: 40px;
      grid-template-rows: 1fr auto;

      ${
        $visitorCanEdit
          ? css`
              grid-template-columns: auto 2fr 1fr;
              grid-template-areas:
                'cardDragIcon cardContent cardActions'
                '. cardTags cardActions';
            `
          : css`
              grid-template-columns: 2.5fr 1fr;
              grid-template-areas:
                'cardContent cardActions'
                'cardTags cardActions';
            `
      }

    ${
      isDragging &&
      css`
        box-shadow: 0 0 20px rgba(0, 0, 0, 0.08);
      `
    }

    ${
      startRevealProjectAnimation
        ? css`
            animation: ${revealLeftToRight} 4s;
          `
        : undefined
    }
  `}
`;

const CardContent = styled.div`
  grid-area: cardContent;
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

const CardMobileActions = styled.div`
  grid-area: mobileActions;
`;

const CardDragIconContainer = styled.div`
  grid-area: cardDragIcon;
  margin-right: 4px;
`;

const CardTags = styled.div`
  grid-area: cardTags;
  display: flex;
  align-items: center;
  flex-flow: wrap;
  gap: 8px;

  ${({ theme }) => css`
    ${theme.mediaQueries.md} {
      margin-top: 24px;
      max-width: 388px;
    }

    ${StyledTagBox} {
      ${Text} {
        font-size: 14px;
      }
    }
  `};
`;

const CardActions = styled.div`
  grid-area: cardActions;
  display: flex;
  justify-content: flex-start;
  align-items: center;
  gap: 24px;

  ${({ theme }) => css`
    ${theme.mediaQueries.md} {
      justify-content: center;
    }
  `};
`;

const CardSortHandle = styled.div<{ withMarginTop: boolean }>`
  ${({ theme, withMarginTop }) => css`
    cursor: move;
    touch-action: none;
    margin-top: ${withMarginTop ? '4px' : '0px'};

    color: ${theme.colorsV2.gray50};
    transition: color 0.2s ease-out;

    :hover {
      color: ${theme.colorsV2.black};
    }
  `};
`;

const Title = styled(Text)``;

const Description = styled(Text)<{ truncateText?: boolean }>`
  ${({ theme, truncateText }) => css`
    color: ${theme.colorsV2.gray70};
    word-break: break-word;

    ${truncateText
      ? css`
          @media screen and (max-width: ${MAX_MAIN_LAYOUT_SIZE}px) {
            display: -webkit-box;
            -webkit-line-clamp: 2;
            -webkit-box-orient: vertical;
            overflow: hidden;
          }
        `
      : undefined}
  `}
`;

const Divider = styled.hr`
  ${({ theme }) => css`
    background-color: ${theme.colorsV2.gray30};
    height: 1px;
    border: 0;
    z-index: 0;
  `}
`;

const LinkedPortfolioProjectsContainer = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
`;

const MenuContainer = styled.div`
  display: flex;
  height: min-content;

  span {
    /* this is to get the menu items to align properly */
    text-align: left;
  }
`;

type ProductizedServiceCardProps = Sortable & {
  readonly canEdit: boolean;
  readonly isLastElement: boolean;
  readonly openInNewWindow?: boolean;
  readonly position: number;
  readonly rolesSortFunction?: (
    a: { name: string },
    b: { name: string },
  ) => number;
  readonly service: ProductizedServiceCardDataWrapper$data;
  readonly tagOverflowMenuPlacement?: 'bottom' | 'left' | 'right' | 'top';
  readonly toolsSortFunction?: (
    a: { name: string },
    b: { name: string },
  ) => number;
};

type RolesAndTools = {
  readonly id: string;
  readonly name: string;
};

export const ProductizedServiceCard = ({
  canEdit,
  disabled = false,
  isDragging,
  isLastElement,
  openInNewWindow,
  position,
  rolesSortFunction,
  service,
  sortableListeners,
  tagOverflowMenuPlacement = 'bottom',
  toolsSortFunction,
}: ProductizedServiceCardProps) => {
  const {
    llmSuggestionId,
    portfolioProjects,
    price,
    publishedAt,
    roles,
    slug,
    title,
    tools,
    visitorCanEdit,
  } = service;

  const shouldDisplaySortHandle = !disabled && canEdit;

  const [editMenuOpen, setEditMenuOpen] = useState(false);

  const toggleMenu = useCallback(() => {
    setEditMenuOpen((open) => !open);
  }, [setEditMenuOpen]);

  const { track } = useAnalytics();

  const [openDeleteModal, onDelete] = useDeleteProductizedService({
    id: service.id,
  });

  const isBelowMedium = useIsMediumScreen();

  const onViewedRef = useOnElementViewed<HTMLDivElement>(() =>
    track('service_card', {
      action: 'viewed',
      id: service.analyticsId,
      is_owner: service.visitorCanEdit,
      owner_id: service.userProfile.analyticsId,
      position_index: position,
      section: 'profile',
    }),
  );

  const onClickTitle = useCallback(() => {
    track('service_card', {
      action: 'tapped',
      id: service.analyticsId,
      is_owner: service.visitorCanEdit,
      owner_id: service.userProfile.analyticsId,
      position_index: position,
      section: 'profile',
    });
  }, [
    position,
    service.analyticsId,
    service.userProfile.analyticsId,
    service.visitorCanEdit,
    track,
  ]);

  const hasSeenProfileMagicRevealAnimation = useHasUserInterfaceFlags(
    'HIDE_FTUE_CONTRA_PROFILE_MAGIC_REVEAL_ANIMATION',
  );

  const isDraft = publishedAt === null;
  const isProfileMagicSuggestion = isDraft && llmSuggestionId !== null;
  const shouldShowProfileMagicRevealAnimation =
    visitorCanEdit &&
    !hasSeenProfileMagicRevealAnimation &&
    isProfileMagicSuggestion;

  const serviceLink =
    isProfileMagicSuggestion && visitorCanEdit
      ? getRoute('/s/@slug/edit', { args: { slug } })
      : getRoute('/s/@slug', {
          args: { slug },
        });

  const handleDeleteSuggestedService = useCallback(() => {
    if (canEdit && isProfileMagicSuggestion) {
      onDelete(() => {}, true);
    }
  }, [canEdit, isProfileMagicSuggestion, onDelete]);

  // edges is a non nullable type, but when I was using the app locally I found it was sometimes null. Adding optional chaining to be safe
  const roleItems = roles?.edges ?? [];
  const toolItems = tools?.edges ?? [];

  const combinedSortFunction =
    toolsSortFunction && rolesSortFunction
      ? (a: { name: string }, b: { name: string }) => {
          const toolSortResult = toolsSortFunction(a, b);
          if (toolSortResult !== 0) return toolSortResult;
          return rolesSortFunction(a, b);
        }
      : null;

  const roleAndToolNodes = [...roleItems, ...toolItems].map((tag) => tag.node);

  const rolesAndTools = combinedSortFunction
    ? roleAndToolNodes.sort(combinedSortFunction)
    : roleAndToolNodes;

  const transformItemToTag = useCallback((item: RolesAndTools): TagMenuItem => {
    return {
      label: item.name,
      value: item.id,
      variant: 'gray20',
    };
  }, []);

  return (
    <>
      <Container
        $visitorCanEdit={visitorCanEdit}
        isDragging={isDragging}
        ref={onViewedRef}
        startRevealProjectAnimation={shouldShowProfileMagicRevealAnimation}
      >
        {shouldDisplaySortHandle && !isBelowMedium ? (
          <CardDragIconContainer>
            <CardSortHandle
              aria-label="Re-order service"
              role="button"
              tabIndex={0}
              withMarginTop={!isDraft}
              {...sortableListeners}
            >
              <MoveIcon size={24} />
            </CardSortHandle>
          </CardDragIconContainer>
        ) : null}
        <CardContent>
          <MaybeDraftAndSuggestedTags
            canEdit={canEdit}
            isDraft={isDraft}
            isProfileMagicSuggestion={isProfileMagicSuggestion}
            variant="Service"
          />
          <Title
            as={Link}
            onClick={onClickTitle}
            textStyle="fifthLevelHeadline"
            to={serviceLink}
            {...(openInNewWindow ? { target: '_blank' } : {})}
          >
            {title}
          </Title>
          {portfolioProjects &&
          portfolioProjects.edges &&
          portfolioProjects.edges.length > 0 ? (
            <LinkedPortfolioProjectsContainer>
              <Description
                textStyle="bodyRegular"
                truncateText={isProfileMagicSuggestion}
              >
                Example projects:
              </Description>
              {portfolioProjects?.edges?.map(({ node }) => (
                <ProductizedServiceCardLinkedProject
                  key={`${service.id}-${node.id}`}
                  portfolioProject={node}
                />
              ))}
            </LinkedPortfolioProjectsContainer>
          ) : null}
          {isBelowMedium ? (
            <ProductizedServiceCardPricing price={price} />
          ) : null}
        </CardContent>
        <CardTags>
          <TagListMenu<RolesAndTools>
            items={rolesAndTools}
            maxRows={2}
            overFlowMenuPlacement={tagOverflowMenuPlacement}
            readOnly
            transformItemToTag={transformItemToTag}
            variant="gray20"
          />
        </CardTags>

        {canEdit || !isBelowMedium ? (
          <CardActions>
            <ProductizedServiceCardDetails
              actions={
                <MenuContainer>
                  <MaybeEditableCardActionsMenuButton
                    canEdit={canEdit}
                    editMenuOpen={editMenuOpen}
                    isProfileMagicSuggestion={isProfileMagicSuggestion}
                    onDismissSuggestion={handleDeleteSuggestedService}
                    openDeleteModal={openDeleteModal}
                    slug={slug}
                    toggleMenu={toggleMenu}
                    variant="service"
                  />
                </MenuContainer>
              }
              position={position}
              service={service}
            />
          </CardActions>
        ) : null}

        {isBelowMedium && canEdit ? (
          <CardMobileActions>
            <MaybeMobileEditableCardActionsMenu
              canEdit={canEdit}
              editMenuOpen={editMenuOpen}
              isProfileMagicSuggestion={isProfileMagicSuggestion}
              onDismissSuggestion={handleDeleteSuggestedService}
              openDeleteModal={openDeleteModal}
              slug={slug}
              sortableListeners={sortableListeners}
              toggleMenu={toggleMenu}
              variant="service"
            />
          </CardMobileActions>
        ) : null}
      </Container>

      {isLastElement ? null : <Divider />}
    </>
  );
};
